import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [newMessage, setNewMessage] = useState(null); // Für neue Nachrichten-Events
  const [workstationUpdate, setWorkstationUpdate] = useState(null); // Für Workstation Login/Logout Events
  const wsRef = useRef(null);
  const userRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const lastSoundPlayedRef = useRef(0); // Timestamp des letzten abgespielten Sounds
  const processedMessagesRef = useRef(new Set()); // Set of processed message IDs

  const connectWebSocket = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders['Authorization'] && !authHeaders['X-Session-ID']) {
      console.log('❌ Kein Auth-Token vorhanden - WebSocket nicht verbunden');
      return;
    }

    // Verhindere mehrfache Verbindungen
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('⚠️ WebSocket bereits verbunden - überspringe');
      return;
    }

    let userId;
    try {
      const meResp = await fetch(createApiUrl('auth/me'), { headers: authHeaders });
      if (meResp.ok) {
        const meData = await meResp.json();
        userId = meData.id;
        userRef.current = meData;
      }
    } catch (e) {
      console.warn('WebSocket: Konnte User-ID nicht laden', e);
    }

    if (!userId) {
      console.log('❌ Kein User eingeloggt - WebSocket nicht verbunden');
      return;
    }

    console.log('🔌 connectWebSocket aufgerufen', {
      userId,
      currentConnectionState: wsRef.current?.readyState,
      timestamp: new Date().toISOString()
    });

    try {
      const apiUrl = createApiUrl(`ws/chat/${userId}`);
      const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      
      console.log('🔌 Verbinde WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket verbunden');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = async (event) => {
        // Ignoriere "pong" Antworten (Keepalive)
        if (event.data === 'pong') {
          return;
        }
        
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket Nachricht empfangen:', data);

          switch (data.type) {
            case 'initial_state':
              // Initiale unread_count vom Server
              setUnreadCount(data.unread_count || 0);
              
              // Verarbeite initiale Locks
              if (data.locks && Array.isArray(data.locks)) {
                console.log('🔒 Initiale Locks empfangen:', data.locks.length);
                data.locks.forEach(lock => {
                  console.log('  → Lock:', lock.type, lock.report_id || lock.exam_id);
                  setNewMessage(lock); // Triggert die Lock-Verarbeitung in den Komponenten
                });
              }
              break;

            case 'unread_count_update':
              // Aktualisiere unread_count
              setUnreadCount(data.unread_count || 0);
              break;

            case 'new_message':
              // Neue Nachricht empfangen
              console.log('💬 Neue Chat-Nachricht:', data, 'Timestamp:', Date.now());
              
              // ✅ Prüfe ob diese Nachricht schon verarbeitet wurde
              const messageKey = `${data.conversation_id}-${data.id || Date.now()}`;
              if (processedMessagesRef.current.has(messageKey)) {
                console.log('🔇 Nachricht bereits verarbeitet, überspringe:', messageKey);
                return;
              }
              processedMessagesRef.current.add(messageKey);
              
              // Cleanup: Alte Nachrichten aus Set entfernen (behalte nur letzte 50)
              if (processedMessagesRef.current.size > 50) {
                const arr = Array.from(processedMessagesRef.current);
                processedMessagesRef.current = new Set(arr.slice(-50));
              }
              
              // Setze neue Nachricht (für Chat-Component)
              setNewMessage(data);
              
              // Aktualisiere unread_count
              setUnreadCount(prev => prev + 1);
              
              // ✅ Sound-Logik mit sofortigem Debouncing (VOR dem async fetch)
              (async () => {
                // 🚨 Sound-Handling-Strategie:
                // 1. Im Cockpit (window.name === 'mtr-dashboard'): Immer abspielen
                // 2. Im MTR-Dashboard (group_id 6, nicht im Cockpit): NICHT abspielen (MTRDashboard.js übernimmt)
                // 3. Normale Benutzer: Immer abspielen
                const isInCockpit = window.name === 'mtr-dashboard';
                const currentUser = userRef.current;
                const isMTRDashboard = currentUser?.group_id === 6 && !isInCockpit;
                
                console.log('🪟 Fenster-Check:', { 
                  windowName: window.name, 
                  isInCockpit, 
                  groupId: currentUser?.group_id,
                  isMTRDashboard 
                });
                
                if (isMTRDashboard) {
                  console.log('🔇 MTR-Dashboard - MTRDashboard.js übernimmt Sound-Handling');
                  return;
                }
                
                // Prüfe SOFORT ob Sound abgespielt werden soll (synchron)
                const now = Date.now();
                const timeSinceLastSound = now - lastSoundPlayedRef.current;
                console.log('🔊 Sound-Check:', { 
                  messageKey,
                  timeSinceLastSound, 
                  threshold: 2000,
                  willPlay: timeSinceLastSound >= 2000 
                });
                
                if (now - lastSoundPlayedRef.current < 2000) {
                  console.log('🔇 Sound-Duplikat verhindert (Handler-Level)');
                  return;
                }
                lastSoundPlayedRef.current = now;
                console.log('✅ Sound wird vorbereitet für:', messageKey);
                
                // Jetzt erst User-Einstellungen laden
                try {
                  const userResponse = await fetch(createApiUrl('auth/me'), {
                    headers: getAuthHeaders()
                  });
                  if (userResponse.ok) {
                    const userData = await userResponse.json();
                    const chatSettings = userData.chat_settings || {};
                    
                    if (!chatSettings.muted) {
                      const soundName = chatSettings.notificationSound || '3Dots';
                      const audioId = Math.random().toString(36).substr(2, 9);
                      console.log(`🔔 [${audioId}] Sound wird erstellt:`, soundName);
                      const audio = new Audio(`${process.env.PUBLIC_URL}/statics/Sounds/${soundName}.mp3`);
                      console.log(`🔔 [${audioId}] Audio-Objekt erstellt, rufe play() auf...`);
                      audio.play()
                        .then(() => console.log(`✅ [${audioId}] Sound erfolgreich abgespielt`))
                        .catch(err => console.warn(`❌ [${audioId}] Konnte Sound nicht abspielen:`, err));
                    } else {
                      console.log('🔇 Sound ist stumm geschaltet (User-Einstellungen)');
                    }
                  } else {
                    // API-Fehler - spiele Standard-Sound
                    const audioId = Math.random().toString(36).substr(2, 9);
                    console.log(`🔔 [${audioId}] Sound wird abgespielt (Fallback)`);
                    const audio = new Audio(`${process.env.PUBLIC_URL}/statics/Sounds/3Dots.mp3`);
                    console.log(`🔔 [${audioId}] Audio-Objekt erstellt, rufe play() auf...`);
                    audio.play()
                      .then(() => console.log(`✅ [${audioId}] Sound erfolgreich abgespielt`))
                      .catch(err => console.warn(`❌ [${audioId}] Konnte Sound nicht abspielen:`, err));
                  }
                } catch (err) {
                  // Netzwerk-Fehler - spiele Standard-Sound
                  console.warn('Fehler beim Abrufen der Chat-Einstellungen:', err);
                  const audioId = Math.random().toString(36).substr(2, 9);
                  console.log(`🔔 [${audioId}] Sound wird abgespielt (Error-Fallback)`);
                  const audio = new Audio(`${process.env.PUBLIC_URL}/statics/Sounds/3Dots.mp3`);
                  console.log(`🔔 [${audioId}] Audio-Objekt erstellt, rufe play() auf...`);
                  audio.play()
                    .then(() => console.log(`✅ [${audioId}] Sound erfolgreich abgespielt`))
                    .catch(err => console.warn(`❌ [${audioId}] Konnte Sound nicht abspielen:`, err));
                }
              })();
              break;
            
            case 'report_opened':
            case 'report_closed':
            case 'exam_opened':
            case 'exam_closed':
              // Lock-Events weiterleiten an Komponenten
              console.log('🔒 Lock-Event:', data.type, data.report_id || data.exam_id);
              setNewMessage(data);
              break;

            case 'workstation_update':
              // Workstation Login/Logout Event
              console.log('🖥️ Workstation-Update:', data.workstation_name, data.action);
              setWorkstationUpdate(data);
              break;

            default:
              console.log('❓ Unbekannter WebSocket Nachrichtentyp:', data.type);
          }
        } catch (err) {
          console.error('❌ Fehler beim Verarbeiten der WebSocket-Nachricht:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket Fehler:', error);
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log('❌ WebSocket geschlossen');
        setIsConnected(false);
        wsRef.current = null;

        // Automatische Wiederverbindung mit Backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`🔄 Versuche Wiederverbindung in ${delay / 1000}s...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connectWebSocket();
          }, delay);
        } else {
          console.log('❌ Max Wiederverbindungsversuche erreicht');
        }
      };

    } catch (err) {
      console.error('❌ Fehler beim Erstellen der WebSocket-Verbindung:', err);
      setIsConnected(false);
    }
  }, []); // Keine Dependencies mehr nötig

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Beim Mount: Verbinde WebSocket mit Retry falls Auth noch nicht bereit
  useEffect(() => {
    let cancelled = false;
    const tryConnect = async (attempt = 0) => {
      if (cancelled) return;
      await connectWebSocket();
      if (!cancelled && !wsRef.current && attempt < 5) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
        setTimeout(() => tryConnect(attempt + 1), delay);
      }
    };

    const initTimeout = setTimeout(() => tryConnect(), 300);

    return () => {
      cancelled = true;
      clearTimeout(initTimeout);
      disconnectWebSocket();
    };
  }, [connectWebSocket, disconnectWebSocket]);

  // Heartbeat/Ping alle 30 Sekunden
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  const refreshUnreadCount = useCallback(async () => {
    // Holt den aktuellen unread_count vom Backend und aktualisiert den State
    try {
      const response = await fetch(createApiUrl('/chat/unread-count'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        console.log('🔄 WebSocket: Unread count vom Backend aktualisiert:', data.unread_count);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('❌ WebSocket: Fehler beim Aktualisieren des unread_count:', error);
    }
  }, []);

  const value = {
    unreadCount,
    isConnected,
    newMessage,
    workstationUpdate,
    clearNewMessage: () => setNewMessage(null),
    clearWorkstationUpdate: () => setWorkstationUpdate(null),
    refreshUnreadCount,
    connectWebSocket
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
